import { HelpIcon } from '@/shared/components/HelpIcon'
import { ResourceSplitBar } from '@/shared/components/ResourceSplitBar'
import { StorageBar } from '@/features/dashboard/components/StorageBar'
import type { TenantAnalytics } from '@/lib/apiTypes'
import { FootprintSummary } from '@/features/dashboard/cards/FootprintSummary'

export function ManagedResourcesCard({
  state,
  footprint,
}: {
  state: TenantAnalytics['state'] | undefined
  footprint: TenantAnalytics['footprint'] | undefined
}) {
  // "Not yet classified" is driven off classified_workspaces, NEVER off a zero sum: a genuine
  // all-internal org is a valid real: 0 and must still render the bar. Field absent => older worker.
  const classified = state?.classified_workspaces
  const real = state?.real_resources
  const internal = state?.internal_resources
  const notClassified = classified === undefined || classified === 0

  return (
    <div className="card fu" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div>
          <div className="kicker">Footprint</div>
          <h3 style={{ fontSize: '0.95rem' }}>Resources At A Glance</h3>
        </div>
        <HelpIcon track="help_resources_at_a_glance" title="Resources At A Glance" kicker="// what this implies">
          <p>Everything this org manages, at a glance: the <strong>resource split</strong>, the
          <strong> storage</strong> your artifacts occupy, and inventory counts (modules, providers,
          retained versions, state files).</p>
          <p>The storage bar is drawn on a <strong>log scale</strong>, because provider binaries
          routinely outweigh module tarballs by a factor of a million - proportional widths would
          render the smaller domains as nothing at all. Segment widths therefore show presence and
          rank, not share; read the byte figures beside each swatch to compare. A domain whose
          artifacts have no recorded size reads <strong>not measured</strong> rather than 0 B.</p>
          <p>Managed resources across your state workspaces are split into <strong>real</strong>
          infrastructure (aws / google / azurerm and other cloud providers) versus
          <strong> internal-only</strong> logical resources (random, tls, null, local, time - the
          glue Terraform manages but that provisions nothing in a cloud).</p>
          <p>A workspace that is mostly internal resources is often config plumbing rather than
          real infrastructure. Tracking the real share gives a truer read of how much live
          footprint you actually manage, filtered from the logical noise. Data sources are
          excluded; counts are instance-level.</p>
        </HelpIcon>
      </div>
      <div style={{ padding: '18px' }}>
        <div className="footprint-split">
          {/* Left: managed-resource real/internal split. */}
          <div>
            <div className="footprint-split-label">managed resources</div>
            {notClassified ? (
              <div style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text3)' }}>
                Not yet classified. New state pushes are split automatically; rescan a workspace to
                classify its existing state.
              </div>
            ) : (
              <ResourceSplitBar real={real} internal={internal} />
            )}
          </div>
          {/* Right: storage split across modules / state / providers. */}
          {footprint && (
            <div>
              <div className="footprint-split-label">storage</div>
              <StorageBar
                module={footprint.module_storage_bytes}
                state={footprint.state_storage_bytes}
                provider={footprint.provider_storage_bytes}
                measured={footprint.storage_measured}
                moduleMeasured={footprint.module_storage_measured}
                stateMeasured={footprint.state_storage_measured}
                providerMeasured={footprint.provider_storage_measured}
              />
            </div>
          )}
        </div>
        <FootprintSummary footprint={footprint} stateFilesCount={state?.total_workspaces} />
      </div>
    </div>
  )
}
